import { Component, Input, ElementRef, ChangeDetectorRef, inject, output, input, viewChild } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { QuillModule } from 'ngx-quill';
import { MaterialModule } from '../../material.module';
import { JkMinioService, MinioImageItem } from '../../services/jk-minio.service';

@Component({
  selector: 'app-jk-rich-editor',
  standalone: true,
  imports: [FormsModule, QuillModule, MaterialModule],
  templateUrl: './jk-rich-editor.component.html',
  styles: [`
    :host { display: block; grid-column: 1 / -1; min-width: 0; width: 100%; }
    .jk-rich-editor { min-width: 0; width: 100%; }
    ::ng-deep .jk-rich-editor .ql-container { width: 100%; }
    ::ng-deep .ql-editor img { max-width: 100%; height: auto; }
  `],
})
export class JkRichEditorComponent {
  // TODO: Skipped for migration because:
  //  Your application code writes to the input. This prevents migration.
  @Input() value: string = '';
  readonly valueChange = output<string>();

  readonly readonly = input<boolean>(false);
  // TODO: Skipped for migration because:
  //  This input is used in a control flow expression (e.g. `@if` or `*ngIf`)
  //  and migrating would break narrowing currently.
  @Input() label: string = '';
  readonly required = input<boolean>(false);
  readonly path = input<string>('');
  readonly host = input<string>('');
  readonly minHeight = input<string>('250px');

  get editorValue(): string {
    return this.value;
  }

  readonly imageInput = viewChild.required<ElementRef<HTMLInputElement>>('imageInput');

  private quillInstance: any;
  showMinioImages = false;
  minioImages: MinioImageItem[] = [];
  minioImagesLoading = false;

  private minioService = inject(JkMinioService);
  private cdr = inject(ChangeDetectorRef);

  quillModules = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'],
        [{ header: [1, 2, 3, false] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ color: [] }, { background: [] }],
        ['link', 'image'],
        ['clean'],
      ],
      handlers: {
        image: () => this.triggerImageUpload(),
      },
    },
  };

  onQuillEditorCreated(quill: any) {
    this.quillInstance = quill;
  }

  triggerImageUpload() {
    this.imageInput()?.nativeElement.click();
  }

  async onImageFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    input.value = '';
    try {
      const url = await this.minioService.uploadImage(this.host(), this.path(), file);
      this.insertImageToEditor(url);
    } catch (e) {
      console.error('上傳圖片至 MinIO 失敗', e);
      alert('上傳圖片失敗，請確認 MinIO 服務已啟動');
    }
  }

  private insertImageToEditor(url: string) {
    if (!this.quillInstance) return;
    const range = this.quillInstance.getSelection(true);
    this.quillInstance.insertEmbed(range.index, 'image', url);
    this.quillInstance.setSelection(range.index + 1);
    this.emitEditorHtml();
  }

  async toggleMinioImages() {
    this.showMinioImages = !this.showMinioImages;
    if (!this.showMinioImages || this.minioImages.length > 0) return;
    this.minioImagesLoading = true;
    this.minioImages = [];
    try {
      this.minioImages = await this.minioService.listImages(this.host(), this.path());
    } catch (e) {
      console.error('載入 MinIO 圖片失敗', e);
    } finally {
      this.minioImagesLoading = false;
      this.cdr.detectChanges();
    }
  }

  insertMinioImage(url: string) {
    this.insertImageToEditor(url);
    this.showMinioImages = false;
  }

  onModelChange(val: string) {
    this.updateValue(val);
  }

  private emitEditorHtml() {
    if (!this.quillInstance) return;
    const html = this.quillInstance.root?.innerHTML || '';
    this.updateValue(html);
  }

  private updateValue(value: string) {
    if (this.value !== value) {
      this.value = value;
      this.valueChange.emit(this.value);
    }
  }
}
